import * as yup from 'yup';
import { useTranslations } from 'hooks/useTranslations';
import { countEditorCharacters } from 'components/organisms/HtmlSlate';
import { EgoodReasonValues } from 'models/licenceConfigurator';
import { ERegExpTypes } from '../../constants/RegExpTypes';

const useFormSchema = () => {
  const { t } = useTranslations();
  const schema = yup.object({
    general: yup.object({
      code: yup
        .string()
        .required(t('general.field.validation.required'))
        .max(20, t('general.field.validation.maxCharacters').replace('{0}', '20'))
        .trim(),
      name: yup
        .string()
        .required(t('general.field.validation.required'))
        .max(100, t('general.field.validation.maxCharacters').replace('{0}', '100'))
        .trim(),
      description: yup
        .string()
        .required(t('general.field.validation.required'))
        .test(
          'editor-validation',
          t('general.field.validation.maxCharacters').replace('{0}', '1000'),
          (value) => countEditorCharacters(value) <= 1000,
        )
        .trim(),
      licenceDurationYears: yup
        .number()
        .typeError(t('licence.validation.fee.numberFormat.message'))
        .max(100, t('general.field.validation.valueIsNotCorrect'))
        .test(
          'required',
          t('general.field.validation.valueIsNotCorrect'),
          (value, obj) =>
            !(
              !obj.parent.licenceDurationUnlimited &&
              (value === undefined || value < 1) &&
              (obj.parent.licenceDurationMonths === undefined ||
                obj.parent.licenceDurationMonths < 1) &&
              (obj.parent.licenceDurationDays === undefined ||
                obj.parent.licenceDurationDays < 1)
            ),
        ),
      licenceDurationMonths: yup
        .number()
        .typeError(t('licence.validation.fee.numberFormat.message'))
        .test(
          'required',
          t('general.field.validation.valueIsNotCorrect'),
          (value, obj) =>
            !(
              !obj.parent.licenceDurationUnlimited &&
              (value === undefined || value < 1) &&
              (obj.parent.licenceDurationYears === undefined ||
                obj.parent.licenceDurationYears < 1) &&
              (obj.parent.licenceDurationDays === undefined ||
                obj.parent.licenceDurationDays < 1)
            ),
        )
        .test(
          'required',
          t('general.field.validation.valueIsNotCorrect'),
          (value) => value !== undefined && value <= 11 && value >= 0,
        ),
      licenceDurationDays: yup
        .number()
        .typeError(t('licence.validation.fee.numberFormat.message'))
        .test(
          'required',
          t('general.field.validation.valueIsNotCorrect'),
          (value, obj) =>
            !(
              !obj.parent.licenceDurationUnlimited &&
              (value === undefined || value < 1) &&
              (obj.parent.licenceDurationYears === undefined ||
                obj.parent.licenceDurationYears < 1) &&
              (obj.parent.licenceDurationMonths === undefined ||
                obj.parent.licenceDurationMonths < 1)
            ),
        )
        .test(
          'required',
          t('general.field.validation.valueIsNotCorrect'),
          (value) => value !== undefined && value <= 30 && value >= 0,
        ),
    }),
    owner: yup.object({
      legalTypes: yup.object().test(
        'at-least-one-true',
        t('general.field.minOneOptionRequired.required'),
        (legalTypesValues: any) =>
          !!legalTypesValues &&
          Object.keys(legalTypesValues).filter((key: any) => {
            return legalTypesValues[key];
          }).length > 0,
      ),
    }),
    conditions: yup.object({
      goodReason: yup.string().required(t('general.field.validation.required')),
      goodReasonDictionaryIds: yup
        .object()
        .test(
          'required',
          t('general.field.validation.required'),
          (value, obj) =>
            !(
              obj.parent.goodReason === EgoodReasonValues.Dictionary &&
              !Object.values(value).some((it) => it === true)
            ),
        ),
    }),
  });

  return { schema };
};

interface IFormPriceSchema {
  isPriceEdit: boolean;
  isSettingsEdit: boolean;
}

const useFormPriceSchema = ({ isPriceEdit, isSettingsEdit }: IFormPriceSchema) => {
  const isNewForm = !isPriceEdit && !isSettingsEdit;
  const { t } = useTranslations();
  const schema = yup.object({
    ...((isNewForm || isPriceEdit) && {
      priceName: yup
        .string()
        .required(t('general.field.validation.required'))
        .max(255, t('general.field.validation.maxCharacters').replace('{0}', '255'))
        .trim(),
    }),
    ...((isNewForm || isPriceEdit) && {
      priceFee: yup
        .number()
        .transform((v, o) => {
          if (typeof o === 'string') {
            const s = o.replace(/\s/g, '').replace(',', '.');
            return s === '' ? undefined : Number(s);
          }
          return v;
        })
        .typeError(t('general.field.validation.valueIsNotCorrect'))
        .min(0, t('general.field.validation.valueIsNotCorrect'))
        .max(1_000_000, t('general.field.validation.valueIsNotCorrect'))
        .test('two-decimals', t('general.field.validation.valueIsNotCorrect'), (value) => {
          if (value === undefined || value === null) return true;
          const regex = new RegExp(ERegExpTypes.max2decimals);
          return regex.test(String(value));
        })
        .required(t('general.field.validation.required')),
    }),
    ...((isNewForm || isSettingsEdit) && {
      processTypeUniqueId: yup.string().required(t('general.field.validation.required')),
      legalEntityType: yup.string().required(t('general.field.validation.required')),
      comment: yup
        .string()
        .max(500, t('general.field.validation.maxCharacters').replace('{0}', '500'))
        .trim(),
    }),
    ...((isNewForm || isPriceEdit) && {
      priceDescription: yup
        .string()
        .test(
          'editor-validation',
          t('general.field.validation.maxCharacters').replace('{0}', '1000'),
          (value) => countEditorCharacters(value) <= 1000,
        )
        .trim(),
    }),
  });

  return { schema };
};

export { useFormSchema, useFormPriceSchema };
